import { useEffect, useState, useCallback } from "react";
import { Loader2, Wallet, Plus, ChevronDown, AlertTriangle } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { fcfa, formatDate } from "../../lib/format";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";

export function LivreurFund() {
  const { user } = useAuth();
  const isManager = user?.role === "patron" || user?.role === "associe";
  const [data, setData] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [rechargeId, setRechargeId] = useState(null);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    const url = isManager ? "/admin/livreur-fund" : "/vendeur/livreur-fund/me";
    api.get(url).then(({ data }) => {
      if (isManager) setData(data);
      else setData({ alert_threshold: data.alert_threshold, vendeurs: [{ seller_id: user?.id, name: user?.name, ...data }] });
    }).catch(() => setData(false));
  }, [isManager, user]);
  useEffect(() => { load(); }, [load]);

  const recharge = async (sellerId) => {
    if (!Number(amount) || Number(amount) <= 0) return toast.error("Montant invalide");
    setSaving(true);
    try {
      await api.post("/admin/livreur-fund/topup", { seller_id: sellerId, amount: Number(amount), note: note.trim() });
      toast.success("Fonds rechargé ✅");
      setRechargeId(null); setAmount(""); setNote("");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (data === null) return <div className="bg-white border border-brand-line rounded-2xl p-6 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-brand" /></div>;
  if (data === false) return null;

  return (
    <div data-testid="livreur-fund" className="bg-white border border-brand-line rounded-2xl p-5 sm:p-6">
      <div className="flex items-center gap-2 mb-4">
        <span className="w-9 h-9 rounded-xl bg-brand/15 text-brand flex items-center justify-center"><Wallet className="w-5 h-5" /></span>
        <div>
          <h2 className="font-heading font-bold text-brand-ink">Fonds livreur (avance)</h2>
          <p className="text-xs text-brand-muted">Avance dédiée au paiement des livreurs. Chaque paiement diminue le solde.</p>
        </div>
      </div>

      <div className="space-y-2">
        {data.vendeurs.map((v) => (
          <div key={v.seller_id} data-testid={`fund-row-${v.seller_id}`} className={`border rounded-xl p-4 ${v.low ? "border-red-300 bg-red-50" : "border-brand-line"}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                {v.low && <AlertTriangle className="w-4 h-4 text-red-600" />}
                <div>
                  <p className="font-semibold text-brand-ink text-sm">{v.name}</p>
                  <p className="text-xs text-brand-muted">Rechargé {fcfa(v.topped_up)} · Payé aux livreurs {fcfa(v.paid_out)}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <p className="text-[11px] text-brand-muted">Solde disponible</p>
                  <p className={`font-heading font-black ${v.low ? "text-red-700" : "text-brand-ink"}`} data-testid={`fund-balance-${v.seller_id}`}>{fcfa(v.balance)}</p>
                </div>
                {isManager && (
                  <button onClick={() => { setRechargeId(rechargeId === v.seller_id ? null : v.seller_id); setAmount(""); setNote(""); }} data-testid={`recharge-btn-${v.seller_id}`}
                    className="bg-brand text-white hover:bg-brand-hover rounded-full px-4 py-2 text-sm font-semibold flex items-center gap-1.5">
                    <Plus className="w-4 h-4" /> Recharger
                  </button>
                )}
              </div>
            </div>

            {v.low && <p className="text-xs text-red-600 mt-2">⚠ Fonds bas (moins de {fcfa(data.alert_threshold)}). Pensez à recharger.</p>}

            {isManager && rechargeId === v.seller_id && (
              <div className="mt-3 grid sm:grid-cols-3 gap-2" data-testid={`recharge-form-${v.seller_id}`}>
                <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Montant (ex : 20000)" data-testid={`recharge-amount-${v.seller_id}`}
                  className="border border-brand-line rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30" />
                <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optionnel)"
                  className="border border-brand-line rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30" />
                <button onClick={() => recharge(v.seller_id)} disabled={saving} data-testid={`recharge-submit-${v.seller_id}`}
                  className="bg-brand text-white hover:bg-brand-hover rounded-lg px-4 py-2.5 font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-60">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Valider la recharge
                </button>
              </div>
            )}

            <button onClick={() => setOpenId(openId === v.seller_id ? null : v.seller_id)} data-testid={`fund-history-toggle-${v.seller_id}`}
              className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-brand hover:gap-2 transition-all">
              Historique du fonds <ChevronDown className={`w-4 h-4 transition-transform ${openId === v.seller_id ? "rotate-180" : ""}`} />
            </button>

            {openId === v.seller_id && (
              <div className="mt-3 grid md:grid-cols-2 gap-4" data-testid={`fund-history-${v.seller_id}`}>
                <div>
                  <p className="text-xs font-semibold text-brand-muted mb-2">RECHARGES (+)</p>
                  {(!v.topups || v.topups.length === 0) ? <p className="text-sm text-brand-muted">Aucune recharge.</p>
                    : v.topups.map((t) => (
                      <div key={t.id} className="flex items-center justify-between border border-brand-line rounded-lg px-3 py-2 mb-1.5 text-sm">
                        <span className="text-green-700 font-semibold">+{fcfa(t.amount)}</span>
                        <span className="text-brand-muted text-xs">{formatDate(t.created_at)} · {t.created_by}</span>
                      </div>
                    ))}
                </div>
                <div>
                  <p className="text-xs font-semibold text-brand-muted mb-2">PAIEMENTS LIVREURS (−)</p>
                  {(!v.payments || v.payments.length === 0) ? <p className="text-sm text-brand-muted">Aucun paiement.</p>
                    : v.payments.map((p, i) => (
                      <div key={i} className="flex items-center justify-between border border-brand-line rounded-lg px-3 py-2 mb-1.5 text-sm">
                        <span className="text-red-600 font-semibold">−{fcfa(p.livreur_fee)}</span>
                        <span className="text-brand-muted text-xs">{p.livreur_name} · {p.order_number}</span>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
